import { useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';

import {
	type EscalationEntity,
	type EscalationsCommonService,
	type FormValues,
} from '../../common/types';

export const useEscalationsCommonController = (service: EscalationsCommonService) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [editingData, setEditingData] = useState<EscalationEntity>();
	const [editingId, setEditingId] = useState('');

	const [isChangeStateModalOpen, setIsChangeStateModalOpen] = useState(false);
	const [isCloning, setIsCloning] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEscalationPolicyModalOpen, setIsEscalationPolicyModalOpen] = useState(false);

	const {
		isCreating,
		isDeleting,
		isUpdating,
		isPartiallyUpdating,

		createEscalation,
		deleteEscalation,
		updateEscalation,
		updatePartialEscalation,
	} = service;

	const openEscalationPolicyModal = () => setIsEscalationPolicyModalOpen(true);
	const closeEscalationPolicyModal = () => {
		setIsEscalationPolicyModalOpen(false);
		setIsCloning(false);
		setEditingData(undefined);
	};

	const openDeleteModal = () => setIsDeleteModalOpen(true);
	const closeDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setIsCloning(false);
		setEditingData(undefined);
	};

	const openChangeStateModal = () => setIsChangeStateModalOpen(true);
	const closeChangeStateModal = () => {
		setIsChangeStateModalOpen(false);
		setIsCloning(false);
		setEditingData(undefined);
	};

	const handleEditClick = (data: EscalationEntity) => {
		setEditingData(data);
		openEscalationPolicyModal();
	};

	const handleCloneClick = (data: EscalationEntity) => {
		setIsCloning(true);
		setEditingData(data);
		openEscalationPolicyModal();
	};

	const handleDeleteClick = (data: EscalationEntity) => {
		setEditingData(data);
		openDeleteModal();
	};

	const handleChangeStateClick = (data: EscalationEntity) => {
		setEditingData(data);
		openChangeStateModal();
	};

	const handleChangeState = () => {
		if (editingData) {
			setEditingId(editingData.id);
			updatePartialEscalation({
				enabled: !editingData.enabled,
				id: editingData.id,
			})
				.then(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'escalationPolicy changedStatus', editingData.id, {
						isEnabled: !editingData.enabled,
					});
				})
				.catch(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'escalationPolicy changeStatusFailed', editingData.id, {
						isEnabled: editingData.enabled,
					});
				})
				.finally(() => setEditingId(''));
			closeChangeStateModal();
		}
	};

	const handleDelete = () => {
		if (editingData) {
			setEditingId(editingData.id);
			deleteEscalation(editingData.id, editingData.name)
				.then(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'escalationPolicy deleted', editingData.id);
				})
				.catch(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'escalationPolicy deleteFailed', editingData.id);
				})
				.finally(() => setEditingId(''));
			closeDeleteModal();
		}
	};

	const handleFormSubmit = (allValues: FormValues) => {
		const { repeatEnabled, ...values } = allValues;
		const shouldRepeat = Boolean(repeatEnabled);

		const event = createAnalyticsEvent({});

		if (editingData && !isCloning) {
			const updatedData = {
				...editingData,
				...values,
				repeat: shouldRepeat ? parseInt(values.repeat) : 0,
				maxRepeatCount: shouldRepeat ? (values.maxRepeatCount?.value as number) : 1,
			};

			updateEscalation(updatedData)
				.then(() => closeEscalationPolicyModal())
				.then(() => {
					fireTrackAnalytics(event, 'escalationPolicy edited', editingData.id, {
						teamId: values.teamId,
					});
				})
				.catch(() => {
					fireTrackAnalytics(event, 'escalationPolicy editFailed', editingData.id, {
						teamId: values.teamId,
					});
				});
		} else {
			createEscalation({
				...values,
				enabled: true,
				repeat: shouldRepeat ? parseInt(values.repeat) : 0,
				maxRepeatCount: shouldRepeat ? (values.maxRepeatCount?.value as number) : 1,
			})
				.then(() => {
					closeEscalationPolicyModal();

					if (isCloning && editingData) {
						fireTrackAnalytics(event, `escalationPolicy cloned`, editingData.id, {
							teamId: values.teamId,
						});

						return;
					}

					fireTrackAnalytics(event, `escalationPolicy added`);
				})
				.catch(() => {
					if (isCloning && editingData) {
						fireTrackAnalytics(event, `escalationPolicy cloneFailed`, editingData.id, {
							teamId: values.teamId,
						});

						return;
					}

					fireTrackAnalytics(event, 'escalationPolicy addFailed', {
						teamId: values.teamId,
					});
				});
		}
	};

	return {
		editingData,
		editingId,
		isChangeStateModalOpen,
		isCloning,
		isCreating,
		isDeleteModalOpen,
		isDeleting,
		isEscalationPolicyModalOpen,
		isUpdating,
		isPartiallyUpdating,
		closeDeleteModal,
		closeEscalationPolicyModal,
		openChangeStateModal,
		closeChangeStateModal,

		handleChangeStateClick,
		handleChangeState,
		handleCloneClick,
		handleDeleteClick,
		handleDelete,
		handleEditClick,
		handleFormSubmit,
		openDeleteModal,
		openEscalationPolicyModal,
	};
};
